import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import type { Column } from "../DataTable";
import { OverflowMenu, type OverflowAction } from "./OverflowMenu";

/**
 * Where a DataTable column goes in the phone list:
 * - `primary`: the row's title line (bold). Default for the first column.
 * - `secondary`: smaller lines under the title. Default for other labelled columns.
 * - `chips`: one wrapping row under the secondary lines, for role, status
 *   and similar chips that would each waste a line.
 * - `trailing`: right edge, for a status chip or a single icon. Default for
 *   columns with no label (desktop icon or action columns).
 * - `hidden`: not shown on phones (its actions usually move to the row menu).
 */
export type PhoneSlot = "primary" | "secondary" | "chips" | "trailing" | "hidden";

interface PhoneListProps<T> {
  columns: Column<T>[];
  data: T[];
  ariaLabel: string;
  getRowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** Per-row actions for the ⋮ menu at the end of each row. */
  rowActions?: (row: T) => OverflowAction[];
  /** Accessible name for a row's ⋮ button; defaults to "More actions". */
  rowActionsLabel?: (row: T) => string;
}

function slotOf<T>(col: Column<T>, index: number): PhoneSlot {
  if (col.phone) return col.phone;
  if (index === 0) return "primary";
  return col.label ? "secondary" : "trailing";
}

/**
 * Phone rendering of DataTable: one stacked row per record, built from the
 * same Column<T> definitions, so a page's columns describe both layouts.
 * Rows keep the `table-row-{key}` test IDs.
 */
export function PhoneList<T>({
  columns,
  data,
  ariaLabel,
  getRowKey,
  onRowClick,
  rowActions,
  rowActionsLabel,
}: PhoneListProps<T>) {
  const slotted = columns.map((col, i) => ({ col, slot: slotOf(col, i) }));
  const pick = (slot: PhoneSlot) => slotted.filter((s) => s.slot === slot).map((s) => s.col);
  const primary = pick("primary");
  const secondary = pick("secondary");
  const chips = pick("chips");
  const trailing = pick("trailing");

  return (
    <Paper role="list" aria-label={ariaLabel}>
      {data.map((row, i) => {
        const key = getRowKey(row);
        const actions = rowActions?.(row) ?? [];
        return (
          <Box
            key={key}
            role="listitem"
            data-testid={`table-row-${key}`}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: 2,
              pr: actions.length ? 0.5 : 2,
              py: 1.5,
              borderTop: i === 0 ? undefined : 1,
              borderColor: "divider",
              cursor: onRowClick ? "pointer" : undefined,
              "&:active": onRowClick ? { bgcolor: "action.hover" } : undefined,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {primary.map((col) => (
                <Cell key={col.id} variant="primary">
                  {col.render(row)}
                </Cell>
              ))}
              {secondary.map((col) => (
                <Cell key={col.id} variant="secondary">
                  {col.render(row)}
                </Cell>
              ))}
              {chips.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                  {chips.map((col) => (
                    <Box key={col.id} sx={{ display: "contents" }}>
                      {col.render(row)}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            {trailing.map((col) => (
              <Box key={col.id} sx={{ flexShrink: 0 }}>
                {col.render(row)}
              </Box>
            ))}
            {actions.length > 0 && (
              <Box sx={{ flexShrink: 0 }}>
                <OverflowMenu
                  actions={actions}
                  ariaLabel={rowActionsLabel?.(row) ?? "More actions"}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </Paper>
  );
}

function Cell({ variant, children }: { variant: "primary" | "secondary"; children: ReactNode }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <Typography
      component="div"
      variant={variant === "primary" ? "body1" : "body2"}
      color={variant === "primary" ? "text.primary" : "text.secondary"}
      sx={{
        fontWeight: variant === "primary" ? 600 : undefined,
        mt: variant === "secondary" ? 0.25 : 0,
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </Typography>
  );
}
